// frontend/src/pages/freelancer/FreelancerProfile.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FreelancerSidebarLayout from "./FreelancerSidebarLayout";
import ShareProfileButton from "../../components/ShareProfileButton";
import axios from "axios";
import "./freelancerStyles.css";

function FreelancerProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Freelancers always have the role "Freelancer"
  const getRoleLabel = useCallback(() => {
    return "Freelancer";
  }, []);

  const isBoothRenterRoute = location.pathname.startsWith(
    "/dashboard/booth-renter",
  );
  const basePath = isBoothRenterRoute
    ? "/dashboard/booth-renter"
    : "/dashboard/freelancer";

  // Security: Get auth headers with validation
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  // Security: Validate API base URL
  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg("Configuration error. Please contact support.");
      return null;
    }
    return baseURL;
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.get(`${baseURL}/api/freelancer/profile`, {
        headers,
      });
      setProfileData(res.data);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else if (
        err?.response?.status === 404 &&
        err?.response?.data?.error === "staff_not_found"
      ) {
        // Check if R7 user needs to complete booth rental signup
        const rolesStr = localStorage.getItem("ogestyle_roles");
        if (rolesStr) {
          try {
            const roles = JSON.parse(rolesStr);
            if (roles.includes("R7")) {
              setErrorMsg(
                "Please complete your booth rental signup to access your profile.",
              );
              return;
            }
          } catch (e) {
          }
        }
        setErrorMsg("Profile not found. Please complete your registration.");
      } else {
        setErrorMsg("Unable to load profile. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, getApiBaseUrl, navigate]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleEditClick = () => {
    navigate(`${basePath}/profile/edit`);
  };

  const roleLabel = getRoleLabel();

  if (loading) {
    return (
      <FreelancerSidebarLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>Loading profile...</p>
        </div>
      </FreelancerSidebarLayout>
    );
  }

  if (errorMsg) {
    return (
      <FreelancerSidebarLayout>
        <div style={{ padding: "40px" }}>
          <div
            style={{
              background: "#fff3f3",
              border: "1px solid #c00",
              borderRadius: "8px",
              padding: "20px",
            }}
          >
            <h3 style={{ color: "#c00", margin: "0 0 10px 0" }}>Error</h3>
            <p style={{ margin: 0 }}>{errorMsg}</p>
            <button
              className="btn-primary"
              style={{ marginTop: "15px" }}
              onClick={fetchProfile}
            >
              Retry
            </button>
          </div>
        </div>
      </FreelancerSidebarLayout>
    );
  }

  if (!profileData) {
    return (
      <FreelancerSidebarLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>Profile not found</p>
        </div>
      </FreelancerSidebarLayout>
    );
  }

  if (!profileData.user || !profileData.staff) {
    return (
      <FreelancerSidebarLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>Loading user data...</p>
        </div>
      </FreelancerSidebarLayout>
    );
  }

  return (
    <FreelancerSidebarLayout>
      <div
        style={{
          padding: isMobile ? "12px" : "24px",
          maxWidth: "100%",
        }}
      >
        {/* Header Bar */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            marginBottom: "24px",
            gap: isMobile ? "12px" : "16px",
          }}
        >
          <button
            onClick={() => navigate(basePath)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 18px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              background: "#fff",
              color: "#333",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s",
              width: isMobile ? "100%" : "auto",
              justifyContent: isMobile ? "center" : "flex-start",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "#f5f5f5";
              e.target.style.borderColor = "#001d4a";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#fff";
              e.target.style.borderColor = "#ddd";
            }}
          >
            <span>←</span> Back to Dashboard
          </button>
          <button
            className="btn-primary"
            onClick={handleEditClick}
            style={{
              fontSize: "14px",
              padding: "10px 24px",
              width: isMobile ? "100%" : "auto",
            }}
          >
            ✏️ Edit Profile
          </button>
        </div>

        {/* Share Profile Link */}
        {profileData.staff?.slug &&
          (() => {
            const origin = window.location.origin;
            const staffSlug = profileData.staff.slug;
            const businessSlug = profileData.staff?.businessSlug;
            const staffRole = profileData.staff?.role;
            const locationSlug =
              profileData.staff?.activeLocation?.slug ||
              profileData.staff?.activeLocationSlug;
            const shareUrl =
              staffRole === "R4" || staffRole === "R7"
                ? `${origin}/freelancer/${staffSlug}`
                : businessSlug && locationSlug
                  ? `${origin}/business/${businessSlug}/${locationSlug}/professional/${staffSlug}`
                  : `${origin}/professional/${staffSlug}`;
            const name =
              `${profileData.user?.firstName || ""} ${profileData.user?.lastName || ""}`.trim();
            return (
              <div
                style={{
                  background: "#f0f6ff",
                  border: "1px solid #c3d9f5",
                  borderRadius: "10px",
                  padding: "16px 20px",
                  marginBottom: "24px",
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: isMobile ? "flex-start" : "center",
                  gap: "12px",
                }}
              >
                <span style={{ fontSize: "20px" }}>🔗</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#001d4a",
                      marginBottom: "4px",
                    }}
                  >
                    Your Public Profile Link
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#2d6da3",
                      wordBreak: "break-all",
                    }}
                  >
                    {shareUrl}
                  </div>
                </div>
                <ShareProfileButton
                  url={shareUrl}
                  title={
                    name ? `${name} on Ogestyle` : "My profile on Ogestyle"
                  }
                />
              </div>
            );
          })()}

        {/* Main Content Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "350px 1fr",
            gap: "24px",
            alignItems: "stretch",
          }}
        >
          {/* Left Column - Profile Card */}
          <div
            className="dashboard-card"
            style={{
              padding: isMobile ? "24px" : "32px",
              position: isMobile ? "static" : "sticky",
              top: "24px",
            }}
          >
            {/* Avatar Section */}
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              {profileData.user.profilePicture ? (
                <img
                  src={profileData.user.profilePicture}
                  alt="Profile"
                  style={{
                    width: isMobile ? 120 : 160,
                    height: isMobile ? 120 : 160,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "5px solid #f0f4f8",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: isMobile ? 120 : 160,
                    height: isMobile ? 120 : 160,
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: isMobile ? 48 : 64,
                    color: "#fff",
                    fontWeight: "bold",
                    border: "5px solid #f0f4f8",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  {profileData.user.firstName?.[0]}
                  {profileData.user.lastName?.[0]}
                </div>
              )}
              <h2
                style={{
                  margin: "20px 0 8px 0",
                  fontSize: isMobile ? "22px" : "26px",
                  color: "#111",
                }}
              >
                {profileData.user.firstName} {profileData.user.lastName}
              </h2>
              <div
                style={{
                  display: "inline-block",
                  padding: "8px 20px",
                  background:
                    "linear-gradient(135deg, #e6f2ff 0%, #d0e7ff 100%)",
                  color: "#1e40af",
                  borderRadius: "24px",
                  fontSize: "14px",
                  fontWeight: "600",
                  boxShadow: "0 2px 6px rgba(59, 130, 246, 0.15)",
                }}
              >
                {roleLabel}
              </div>
              {profileData.staff.rating > 0 && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "12px",
                    background: "#fef9e7",
                    borderRadius: "12px",
                    display: "inline-block",
                  }}
                >
                  <div style={{ fontSize: "24px", marginBottom: "4px" }}>
                    ⭐
                  </div>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: "700",
                      color: "#111",
                    }}
                  >
                    {profileData.staff.rating.toFixed(1)}
                  </div>
                  <div style={{ fontSize: "12px", color: "#666" }}>Rating</div>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div
              style={{
                borderTop: "1px solid #e5e7eb",
                paddingTop: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px",
                    background: "#f9fafb",
                    borderRadius: "8px",
                  }}
                >
                  <span style={{ fontSize: "20px" }}>
                    {profileData.staff.status === "Active" ? "✅" : "⚠️"}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#666",
                        marginBottom: "2px",
                      }}
                    >
                      Status
                    </div>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "4px 12px",
                        background:
                          profileData.staff.status === "Active"
                            ? "#d1fae5"
                            : "#fee2e2",
                        color:
                          profileData.staff.status === "Active"
                            ? "#065f46"
                            : "#991b1b",
                        borderRadius: "6px",
                        fontSize: "13px",
                        fontWeight: "600",
                      }}
                    >
                      {profileData.staff.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Information Sections */}
          <div
            style={{ display: "flex", flexDirection: "column", gap: "24px" }}
          >
            {/* Personal Information Card */}
            <div
              className="dashboard-card"
              style={{ padding: isMobile ? "24px" : "32px" }}
            >
              <h3
                style={{
                  margin: "0 0 20px 0",
                  color: "#111",
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span style={{ fontSize: "28px" }}>👤</span>
                Personal Information
              </h3>
              <div
                style={{
                  display: "flex",
                  flexWrap: isMobile ? "wrap" : "nowrap",
                  gap: isMobile ? "16px" : "24px",
                }}
              >
                {[
                  {
                    label: "First Name",
                    value: profileData.user.firstName || "Not provided",
                    flex: 1,
                  },
                  {
                    label: "Last Name",
                    value: profileData.user.lastName || "Not provided",
                    flex: 1,
                  },
                  { label: "Email Address", value: profileData.user.email, flex: 2 },
                  {
                    label: "Phone Number",
                    value: profileData.user.phone || "Not provided",
                    flex: 1,
                  },
                ].map(({ label, value, flex }) => (
                  <div
                    key={label}
                    style={{
                      padding: "16px",
                      background: "#f9fafb",
                      borderRadius: "12px",
                      border: "1px solid #e5e7eb",
                      flex: isMobile ? "1 1 100%" : flex,
                      minWidth: 0,
                    }}
                  >
                    <label
                      style={{
                        display: "block",
                        fontSize: "12px",
                        color: "#6b7280",
                        marginBottom: "6px",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {label}
                    </label>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#111",
                        wordBreak: "break-word",
                      }}
                    >
                      {value}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Role Details Card */}
            <div
              className="dashboard-card"
              style={{ padding: isMobile ? "24px" : "32px" }}
            >
              <h3
                style={{
                  margin: "0 0 20px 0",
                  color: "#111",
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span style={{ fontSize: "28px" }}>💼</span>
                {isBoothRenterRoute ? "Booth Renter" : "Freelancer"} Details
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: isMobile ? "16px" : "24px",
                }}
              >
                <div
                  style={{
                    padding: "16px",
                    background: "#f0f9ff",
                    borderRadius: "12px",
                    border: "1px solid #bfdbfe",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      fontSize: "12px",
                      color: "#1e40af",
                      marginBottom: "6px",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Role
                  </label>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#111",
                      wordBreak: "break-word",
                    }}
                  >
                    {roleLabel}
                  </p>
                </div>
                {profileData.staff.shop && (
                  <>
                    <div
                      style={{
                        padding: "16px",
                        background: "#f0f9ff",
                        borderRadius: "12px",
                        border: "1px solid #bfdbfe",
                      }}
                    >
                      <label
                        style={{
                          display: "block",
                          fontSize: "12px",
                          color: "#1e40af",
                          marginBottom: "6px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Shop Name
                      </label>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#111",
                          wordBreak: "break-word",
                        }}
                      >
                        {profileData.staff.businessName || "—"}
                      </p>
                    </div>
                    <div
                      style={{
                        padding: "16px",
                        background: "#f0f9ff",
                        borderRadius: "12px",
                        border: "1px solid #bfdbfe",
                      }}
                    >
                      <label
                        style={{
                          display: "block",
                          fontSize: "12px",
                          color: "#1e40af",
                          marginBottom: "6px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Location
                      </label>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#111",
                          wordBreak: "break-word",
                        }}
                      >
                        {profileData.staff.shop.street
                          ? `${profileData.staff.shop.street}, `
                          : ""}
                        {profileData.staff.shop.city},{" "}
                        {profileData.staff.shop.state}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </FreelancerSidebarLayout>
  );
}

export default FreelancerProfile;
